/**
 * Google Ads AI Optimization Engine
 * Provides AI-powered recommendations and automated optimizations for Google Ads campaigns
 */

import { GoogleAdsClient, type KeywordMetrics, type CampaignMetrics, type SearchTerm, type QualityScoreData } from './google-ads-client';
import { supabase } from './supabase';

// ============================================================================
// Types & Interfaces
// ============================================================================

export interface BidRecommendation {
  keywordId: string;
  keywordText: string;
  currentBid: number;
  recommendedBid: number;
  reason: string;
  expectedImpact: {
    impressions: string;
    clicks: string;
    conversions: string;
  };
  confidence: number;
  priority: 'high' | 'medium' | 'low';
}

export interface BudgetRecommendation {
  campaignId: string;
  campaignName: string;
  currentBudget: number;
  recommendedBudget: number;
  reason: string;
  expectedROI: number;
  confidence: number;
}

export interface NegativeKeyword {
  searchTerm: string;
  reason: string;
  wastedSpend: number;
  impressions: number;
  clicks: number;
  conversions: number;
  confidence: number;
}

export interface QualityScoreImprovement {
  keywordId: string;
  keywordText: string;
  currentScore: number;
  targetScore: number;
  improvements: {
    adRelevance?: string[];
    landingPageExperience?: string[];
    expectedCTR?: string[];
  };
  estimatedImpact: string;
}

export interface CampaignHealthIssue {
  severity: 'critical' | 'warning' | 'info';
  type: string;
  message: string;
  affectedCampaigns: string[];
  recommendation: string;
  estimatedImpact: string;
}

export interface AIOptimizationReport {
  generatedAt: string;
  customerId: string;
  bidRecommendations: BidRecommendation[];
  budgetRecommendations: BudgetRecommendation[];
  negativeKeywords: NegativeKeyword[];
  qualityScoreImprovements: QualityScoreImprovement[];
  campaignHealthIssues: CampaignHealthIssue[];
  overallScore: number;
  insights: string[];
}

// ============================================================================
// Google Ads AI Optimizer Class
// ============================================================================

export class GoogleAdsAIOptimizer {
  private adsClient: GoogleAdsClient;

  constructor(adsClient: GoogleAdsClient) {
    this.adsClient = adsClient;
  }

  // ==========================================================================
  // Main Optimization Analysis
  // ==========================================================================

  /**
   * Generate comprehensive AI optimization report
   */
  async generateOptimizationReport(
    customerId: string,
    dateRange: { startDate: string; endDate: string }
  ): Promise<AIOptimizationReport> {
    console.log('🤖 Starting AI optimization analysis...');

    // Collect all performance data
    const [keywords, campaigns, searchTerms, qualityScores] = await Promise.all([
      this.adsClient.getKeywordMetrics(customerId, { dateRange }),
      this.adsClient.getCampaignMetrics(customerId, [], dateRange),
      this.adsClient.getSearchTerms(customerId, { dateRange }),
      this.adsClient.getQualityScores(customerId, {}),
    ]);

    // Generate AI-powered recommendations
    const [
      bidRecommendations,
      budgetRecommendations,
      negativeKeywords,
      qualityScoreImprovements,
      campaignHealthIssues,
    ] = await Promise.all([
      this.analyzeBidOpportunities(keywords),
      this.analyzeBudgetAllocation(campaigns),
      this.detectNegativeKeywords(searchTerms),
      this.analyzeQualityScores(qualityScores, keywords),
      this.analyzeCampaignHealth(campaigns, keywords),
    ]);

    // Generate strategic insights using Claude AI
    const insights = await this.generateStrategicInsights({
      keywords,
      campaigns,
      searchTerms,
      bidRecommendations,
      budgetRecommendations,
      negativeKeywords,
    });

    // Calculate overall optimization score
    const overallScore = this.calculateOverallScore({
      campaigns,
      keywords,
      qualityScores,
    });

    const report: AIOptimizationReport = {
      generatedAt: new Date().toISOString(),
      customerId,
      bidRecommendations,
      budgetRecommendations,
      negativeKeywords,
      qualityScoreImprovements,
      campaignHealthIssues,
      overallScore,
      insights,
    };

    // Save report to database
    await this.saveReport(report);

    console.log('✅ AI optimization report generated');
    return report;
  }

  // ==========================================================================
  // Bid Optimization Analysis
  // ==========================================================================

  /**
   * Analyze keyword performance and generate bid recommendations
   */
  private async analyzeBidOpportunities(
    keywords: KeywordMetrics[]
  ): Promise<BidRecommendation[]> {
    const recommendations: BidRecommendation[] = [];

    for (const keyword of keywords) {
      // Skip if no data
      if (keyword.impressions === 0) continue;

      const currentBid = keyword.cpcBidMicros / 1000000;
      const avgCpc = keyword.avgCpcMicros / 1000000;
      const { ctr, conversions, clicks } = keyword;
      const conversionRate = clicks > 0 ? conversions / clicks : 0;

      // Analyze bid opportunity
      let recommendedBid = currentBid;
      let reason = '';
      let priority: 'high' | 'medium' | 'low' = 'low';
      let confidence = 0;

      // High-performing keywords - increase bids
      if (conversions > 0 && conversionRate > 0.03 && ctr > 0.05) {
        const increase = Math.min(currentBid * 0.3, 2); // Max 30% or $2
        recommendedBid = currentBid + increase;
        reason = 'High conversion rate and CTR - increase bid to capture more volume';
        priority = 'high';
        confidence = 0.85;
      }
      // Underperforming keywords - decrease bids
      else if (clicks > 20 && conversions === 0) {
        recommendedBid = currentBid * 0.7; // Decrease by 30%
        reason = 'No conversions despite clicks - reduce spend on underperforming keyword';
        priority = 'high';
        confidence = 0.9;
      }
      // Low CTR - adjust bid
      else if (keyword.impressions > 100 && ctr < 0.02) {
        recommendedBid = currentBid * 0.8; // Decrease by 20%
        reason = 'Low CTR indicates poor ad relevance - reduce bid or improve ad copy';
        priority = 'medium';
        confidence = 0.75;
      }
      // Good performance but overpaying
      else if (avgCpc > 0 && avgCpc < currentBid * 0.7) {
        recommendedBid = avgCpc * 1.1; // Set to 110% of avg CPC
        reason = 'Current bid is much higher than actual CPC - optimize spend';
        priority = 'medium';
        confidence = 0.8;
      }

      // Only add if there's a meaningful change (>5%)
      if (Math.abs(recommendedBid - currentBid) / currentBid > 0.05) {
        recommendations.push({
          keywordId: keyword.keywordId,
          keywordText: keyword.keywordText,
          currentBid: currentBid,
          recommendedBid: Math.round(recommendedBid * 100) / 100,
          reason,
          expectedImpact: this.estimateBidImpact(keyword, recommendedBid),
          confidence,
          priority,
        });
      }
    }

    // Sort by priority and confidence
    return recommendations.sort((a, b) => {
      const priorityOrder = { high: 3, medium: 2, low: 1 };
      return priorityOrder[b.priority] - priorityOrder[a.priority] || b.confidence - a.confidence;
    });
  }

  /**
   * Estimate impact of bid changes
   */
  private estimateBidImpact(
    keyword: KeywordMetrics,
    newBid: number
  ): { impressions: string; clicks: string; conversions: string } {
    const currentBid = keyword.cpcBidMicros / 1000000;
    const bidChange = (newBid - currentBid) / currentBid;

    // Simple estimation model
    const impressionChange = bidChange * 0.5; // 50% correlation
    const clickChange = bidChange * 0.7; // 70% correlation
    const conversionChange = bidChange * 0.4; // 40% correlation

    return {
      impressions: `${impressionChange > 0 ? '+' : ''}${(impressionChange * 100).toFixed(0)}%`,
      clicks: `${clickChange > 0 ? '+' : ''}${(clickChange * 100).toFixed(0)}%`,
      conversions: `${conversionChange > 0 ? '+' : ''}${(conversionChange * 100).toFixed(0)}%`,
    };
  }

  // ==========================================================================
  // Budget Optimization Analysis
  // ==========================================================================

  /**
   * Analyze campaign budgets and recommend reallocation
   */
  private async analyzeBudgetAllocation(
    campaigns: CampaignMetrics[]
  ): Promise<BudgetRecommendation[]> {
    // Group by campaign and calculate totals
    const campaignTotals = new Map<string, CampaignMetrics>();
    
    for (const metric of campaigns) {
      const existing = campaignTotals.get(metric.campaignId);
      if (existing) {
        existing.impressions += metric.impressions;
        existing.clicks += metric.clicks;
        existing.costMicros += metric.costMicros;
        existing.conversions += metric.conversions;
        existing.conversionValue += metric.conversionValue;
      } else {
        campaignTotals.set(metric.campaignId, { ...metric });
      }
    }

    const recommendations: BudgetRecommendation[] = [];

    for (const [campaignId, totals] of campaignTotals) {
      const cost = totals.costMicros / 1000000;
      const roas = cost > 0 ? totals.conversionValue / cost : 0;
      const cpa = totals.conversions > 0 ? cost / totals.conversions : 0;

      // High ROAS campaigns - increase budget
      if (roas > 4 && totals.conversions > 5) {
        recommendations.push({
          campaignId,
          campaignName: totals.campaignName,
          currentBudget: cost,
          recommendedBudget: cost * 1.5,
          reason: `Excellent ROAS of ${roas.toFixed(2)}x - scale up to maximize returns`,
          expectedROI: roas,
          confidence: 0.9,
        });
      }
      // Poor performance - reduce budget
      else if (totals.conversions === 0 && cost > 100) {
        recommendations.push({
          campaignId,
          campaignName: totals.campaignName,
          currentBudget: cost,
          recommendedBudget: cost * 0.5,
          reason: 'No conversions with significant spend - reduce budget and optimize',
          expectedROI: 0,
          confidence: 0.85,
        });
      }
    }

    return recommendations;
  }

  // ==========================================================================
  // Negative Keyword Detection
  // ==========================================================================

  /**
   * Detect search terms that should be added as negative keywords
   */
  private async detectNegativeKeywords(
    searchTerms: SearchTerm[]
  ): Promise<NegativeKeyword[]> {
    const negatives: NegativeKeyword[] = [];

    for (const term of searchTerms) {
      const cost = term.costMicros / 1000000;
      const conversionRate = term.clicks > 0 ? term.conversions / term.clicks : 0;

      // Detect waste patterns
      let isNegative = false;
      let reason = '';
      let confidence = 0;

      // No conversions with significant clicks
      if (term.clicks >= 10 && term.conversions === 0) {
        isNegative = true;
        reason = 'No conversions despite multiple clicks - wasting ad spend';
        confidence = 0.85;
      }
      // Very low CTR indicates irrelevance
      else if (term.impressions >= 50 && term.ctr < 0.005) {
        isNegative = true;
        reason = 'Extremely low CTR indicates search intent mismatch';
        confidence = 0.8;
      }
      // High cost with no results
      else if (cost > 20 && term.conversions === 0) {
        isNegative = true;
        reason = 'High cost with zero conversions - immediate action needed';
        confidence = 0.9;
      }

      // Check for common negative patterns
      const lowQualityPatterns = ['free', 'cheap', 'how to', 'diy', 'jobs', 'careers', 'salary'];
      if (lowQualityPatterns.some(pattern => term.searchTerm.toLowerCase().includes(pattern))) {
        if (conversionRate < 0.01) {
          isNegative = true;
          reason = `Low-intent search term with poor conversion rate`;
          confidence = 0.75;
        }
      }

      if (isNegative) {
        negatives.push({
          searchTerm: term.searchTerm,
          reason,
          wastedSpend: cost,
          impressions: term.impressions,
          clicks: term.clicks,
          conversions: term.conversions,
          confidence,
        });
      }
    }

    // Sort by wasted spend
    return negatives.sort((a, b) => b.wastedSpend - a.wastedSpend);
  }

  // ==========================================================================
  // Quality Score Analysis
  // ==========================================================================

  /**
   * Analyze quality scores and suggest improvements
   */
  private async analyzeQualityScores(
    qualityScores: QualityScoreData[],
    keywords: KeywordMetrics[]
  ): Promise<QualityScoreImprovement[]> {
    const improvements: QualityScoreImprovement[] = [];

    for (const qs of qualityScores) {
      if (qs.qualityScore >= 7) continue; // Skip good quality scores

      const keyword = keywords.find(k => k.keywordId === qs.keywordId);
      if (!keyword) continue;

      const improvement: QualityScoreImprovement = {
        keywordId: qs.keywordId,
        keywordText: qs.keywordText,
        currentScore: qs.qualityScore,
        targetScore: Math.min(10, qs.qualityScore + 3),
        improvements: {},
        estimatedImpact: '',
      };

      // Analyze each component
      if (qs.creativeQualityScore === 'BELOW_AVERAGE') {
        improvement.improvements.adRelevance = [
          'Include keyword in ad headline and description',
          'Test multiple ad variations with different messaging',
          'Ensure ad copy addresses user search intent',
        ];
      }

      if (qs.landingPageExperienceQualityScore === 'BELOW_AVERAGE') {
        improvement.improvements.landingPageExperience = [
          'Improve landing page load speed (target <3s)',
          'Make landing page mobile-friendly',
          'Ensure landing page content matches ad promise',
          'Add clear call-to-action above the fold',
        ];
      }

      if (qs.searchPredictedCtr === 'BELOW_AVERAGE') {
        improvement.improvements.expectedCTR = [
          'Use ad extensions (sitelinks, callouts, structured snippets)',
          'Test emotional triggers in ad copy',
          'Add unique selling propositions to ads',
          'Use dynamic keyword insertion when appropriate',
        ];
      }

      // Estimate CPC improvement
      const potentialCpcReduction = (10 - qs.qualityScore) * 0.05; // ~5% per point
      improvement.estimatedImpact = `Potential CPC reduction: ${(potentialCpcReduction * 100).toFixed(0)}%`;

      improvements.push(improvement);
    }

    return improvements.sort((a, b) => a.currentScore - b.currentScore);
  }

  // ==========================================================================
  // Campaign Health Monitoring
  // ==========================================================================

  /**
   * Analyze overall campaign health and detect issues
   */
  private async analyzeCampaignHealth(
    campaigns: CampaignMetrics[],
    keywords: KeywordMetrics[]
  ): Promise<CampaignHealthIssue[]> {
    const issues: CampaignHealthIssue[] = [];

    // Group by campaign
    const campaignMap = new Map<string, { metrics: CampaignMetrics[]; keywords: KeywordMetrics[] }>();
    
    for (const metric of campaigns) {
      if (!campaignMap.has(metric.campaignId)) {
        campaignMap.set(metric.campaignId, { metrics: [], keywords: [] });
      }
      campaignMap.get(metric.campaignId)!.metrics.push(metric);
    }

    for (const keyword of keywords) {
      if (!campaignMap.has(keyword.campaignId)) {
        campaignMap.set(keyword.campaignId, { metrics: [], keywords: [] });
      }
      campaignMap.get(keyword.campaignId)!.keywords.push(keyword);
    }

    // Analyze each campaign
    for (const [campaignId, data] of campaignMap) {
      const totalImpressions = data.metrics.reduce((sum, m) => sum + m.impressions, 0);
      const totalClicks = data.metrics.reduce((sum, m) => sum + m.clicks, 0);
      const totalCost = data.metrics.reduce((sum, m) => sum + m.costMicros, 0) / 1000000;
      const totalConversions = data.metrics.reduce((sum, m) => sum + m.conversions, 0);
      const avgCtr = totalImpressions > 0 ? totalClicks / totalImpressions : 0;

      // Check for critical issues
      if (totalImpressions === 0 && totalCost > 0) {
        issues.push({
          severity: 'critical',
          type: 'NO_IMPRESSIONS',
          message: 'Campaign has spend but no impressions',
          affectedCampaigns: [data.metrics[0]?.campaignName || campaignId],
          recommendation: 'Check targeting settings, bids, and ad approval status',
          estimatedImpact: `$${totalCost.toFixed(2)} wasted`,
        });
      }

      // Low CTR warning
      if (totalImpressions > 1000 && avgCtr < 0.01) {
        issues.push({
          severity: 'warning',
          type: 'LOW_CTR',
          message: 'Campaign CTR is below industry average',
          affectedCampaigns: [data.metrics[0]?.campaignName || campaignId],
          recommendation: 'Improve ad copy, test new headlines, add ad extensions',
          estimatedImpact: 'Missing out on potential clicks and conversions',
        });
      }

      // No conversions with significant spend
      if (totalCost > 100 && totalConversions === 0) {
        issues.push({
          severity: 'critical',
          type: 'NO_CONVERSIONS',
          message: 'Significant spend with zero conversions',
          affectedCampaigns: [data.metrics[0]?.campaignName || campaignId],
          recommendation: 'Review conversion tracking, landing pages, and targeting',
          estimatedImpact: `$${totalCost.toFixed(2)} without ROI`,
        });
      }

      // Few active keywords
      if (data.keywords.length > 0 && data.keywords.length < 5) {
        issues.push({
          severity: 'info',
          type: 'LIMITED_KEYWORDS',
          message: 'Campaign has very few active keywords',
          affectedCampaigns: [data.metrics[0]?.campaignName || campaignId],
          recommendation: 'Expand keyword list to increase reach and gather more data',
          estimatedImpact: 'Limited reach and testing opportunities',
        });
      }
    }

    return issues.sort((a, b) => {
      const severityOrder = { critical: 3, warning: 2, info: 1 };
      return severityOrder[b.severity] - severityOrder[a.severity];
    });
  }

  // ==========================================================================
  // AI Strategic Insights
  // ==========================================================================

  /**
   * Generate strategic insights using backend AI proxy (Anthropic)
   * No client-side API keys are used.
   */
  private async generateStrategicInsights(data: {
    keywords: KeywordMetrics[];
    campaigns: CampaignMetrics[];
    searchTerms: SearchTerm[];
    bidRecommendations: BidRecommendation[];
    budgetRecommendations: BudgetRecommendation[];
    negativeKeywords: NegativeKeyword[];
  }): Promise<string[]> {
    try {
      const apiBase = import.meta.env.VITE_MARKETING_API_URL;
      if (!apiBase) {
        console.warn('[GoogleAdsAIOptimizer] Backend API URL not configured');
        return [
          'Backend API URL not configured.',
          'Configure VITE_MARKETING_API_URL to enable AI insights.',
        ];
      }

      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) {
        console.warn('[GoogleAdsAIOptimizer] Not authenticated');
        return [
          'User not authenticated; using fallback insights.',
          'Log in to enable AI-generated insights.',
        ];
      }

      // Pre-compute values to avoid complex template expressions
      const totalCampaigns = new Set(data.campaigns.map(c => c.campaignId)).size;
      const totalImpressions = data.campaigns.reduce((sum, c) => sum + c.impressions, 0).toLocaleString();
      const totalClicks = data.campaigns.reduce((sum, c) => sum + c.clicks, 0).toLocaleString();
      const totalCost = (data.campaigns.reduce((sum, c) => sum + c.costMicros, 0) / 1_000_000).toFixed(2);
      const totalConversions = data.campaigns.reduce((sum, c) => sum + c.conversions, 0);
      const activeKeywords = data.keywords.length;
      const avgCtrPct = ((data.keywords.reduce((sum, k) => sum + k.ctr, 0) / Math.max(1, data.keywords.length)) * 100).toFixed(2);
      const bidCount = data.bidRecommendations.length;
      const budgetCount = data.budgetRecommendations.length;
      const negativesCount = data.negativeKeywords.length;

      const promptLines = [
        'Analyze this Google Ads performance data and provide 3-5 strategic insights and actionable recommendations:',
        '',
        'CAMPAIGN PERFORMANCE:',
        `- Total campaigns: ${totalCampaigns}`,
        `- Total impressions: ${totalImpressions}`,
        `- Total clicks: ${totalClicks}`,
        `- Total cost: $${totalCost}`,
        `- Total conversions: ${totalConversions}`,
        '',
        'KEYWORDS:',
        `- Active keywords: ${activeKeywords}`,
        `- Avg CTR: ${avgCtrPct}%`,
        '',
        'OPTIMIZATION OPPORTUNITIES:',
        `- Bid adjustments recommended: ${bidCount}`,
        `- Budget reallocations recommended: ${budgetCount}`,
        `- Negative keywords to add: ${negativesCount}`,
        '',
        'Provide insights as a JSON array of strings. Focus on:',
        '1. Overall performance trends',
        '2. Biggest opportunities for improvement',
        '3. Strategic recommendations',
        '4. Risk areas to address',
        '',
        'Keep each insight concise (2-3 sentences).'
      ];
      const prompt = promptLines.join('\n');

      const response = await fetch(`${apiBase}/api/marketing/ai/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          provider: 'anthropic',
          model_name: 'claude-3-5-sonnet-20240620',
          prompt
        })
      });

      if (!response.ok) {
        const errText = await response.text();
        console.error('[GoogleAdsAIOptimizer] Backend Anthropic proxy error:', errText);
        throw new Error(errText || 'AI proxy error');
      }

      const result = await response.json();
      const text = typeof result?.data === 'string'
        ? result.data
        : result?.data?.text || result?.data?.raw || '';

      // Try to parse as JSON array first
      if (text) {
        try {
          const parsed = JSON.parse(text);
          if (Array.isArray(parsed)) return parsed as string[];
        } catch {
          // not JSON array; attempt to extract array substring
          const match = text.match(/\[[\s\S]*\]/);
          if (match) {
            try {
              const parsedMatch = JSON.parse(match[0]);
              if (Array.isArray(parsedMatch)) return parsedMatch as string[];
            } catch {
              // continue to fallback
            }
          }
        }
      }

      // Fallback: split by lines
      const lines = (text || '').split('\n').map((s: string) => s.trim()).filter(Boolean);
      if (lines.length > 0) return lines;

    } catch (error) {
      console.error('Error generating AI insights:', error);
    }

    // Fallback insights
    return [
      'Campaign performance data collected successfully',
      'Multiple optimization opportunities identified',
      'Review recommendations to improve ROI',
    ];
  }

  // ==========================================================================
  // Scoring & Persistence
  // ==========================================================================

  /**
   * Calculate overall optimization score (0-100)
   */
  private calculateOverallScore(data: {
    campaigns: CampaignMetrics[];
    keywords: KeywordMetrics[];
    qualityScores: QualityScoreData[];
  }): number {
    let score = 50; // Start at 50

    // Calculate metrics
    const totalImpressions = data.campaigns.reduce((sum, c) => sum + c.impressions, 0);
    const totalClicks = data.campaigns.reduce((sum, c) => sum + c.clicks, 0);
    const totalCost = data.campaigns.reduce((sum, c) => sum + c.costMicros, 0) / 1000000;
    const totalConversions = data.campaigns.reduce((sum, c) => sum + c.conversions, 0);
    
    const avgCtr = totalImpressions > 0 ? totalClicks / totalImpressions : 0;
    const conversionRate = totalClicks > 0 ? totalConversions / totalClicks : 0;
    const avgQualityScore = data.qualityScores.length > 0
      ? data.qualityScores.reduce((sum, qs) => sum + qs.qualityScore, 0) / data.qualityScores.length
      : 5;

    // CTR scoring (+/- 20 points)
    if (avgCtr > 0.05) score += 20;
    else if (avgCtr > 0.03) score += 10;
    else if (avgCtr > 0.01) score += 5;
    else score -= 10;

    // Conversion rate scoring (+/- 20 points)
    if (conversionRate > 0.05) score += 20;
    else if (conversionRate > 0.03) score += 10;
    else if (conversionRate > 0.01) score += 5;
    else if (totalClicks > 50) score -= 10;

    // Quality score scoring (+/- 15 points)
    if (avgQualityScore >= 8) score += 15;
    else if (avgQualityScore >= 6) score += 5;
    else if (avgQualityScore < 5) score -= 15;

    // Cost efficiency (+/- 15 points)
    if (totalConversions > 0) {
      const cpa = totalCost / totalConversions;
      if (cpa < 50) score += 15;
      else if (cpa < 100) score += 5;
      else if (cpa > 200) score -= 10;
    }

    // Ensure score is within bounds
    return Math.max(0, Math.min(100, Math.round(score)));
  }

  /**
   * Save optimization report to database
   */
  private async saveReport(report: AIOptimizationReport): Promise<void> {
    try {
      const { error } = await supabase
        .from('google_ads_recommendations')
        .insert({
          customer_id: report.customerId,
          recommendation_type: 'ai_optimization_report',
          title: 'AI Optimization Report',
          description: `Generated ${report.bidRecommendations.length} bid recommendations, ${report.budgetRecommendations.length} budget recommendations`,
          impact_score: report.overallScore,
          data: report,
          status: 'pending',
          generated_at: report.generatedAt,
        });

      if (error) {
        console.error('Error saving optimization report:', error);
      }
    } catch (error) {
      console.error('Error saving report to database:', error);
    }
  }

  // ==========================================================================
  // Automated Actions
  // ==========================================================================

  /**
   * Apply bid recommendations automatically (with safety limits)
   */
  async applyBidRecommendations(
    customerId: string,
    recommendations: BidRecommendation[],
    options: {
      minConfidence?: number;
      maxBidChange?: number;
      dryRun?: boolean;
    } = {}
  ): Promise<{ applied: number; skipped: number; errors: string[] }> {
    const { minConfidence = 0.8, maxBidChange = 0.5, dryRun = true } = options;
    
    let applied = 0;
    let skipped = 0;
    const errors: string[] = [];

    for (const rec of recommendations) {
      // Safety checks
      if (rec.confidence < minConfidence) {
        skipped++;
        continue;
      }

      const bidChangePercent = Math.abs(rec.recommendedBid - rec.currentBid) / rec.currentBid;
      if (bidChangePercent > maxBidChange) {
        skipped++;
        errors.push(`${rec.keywordText}: Bid change too large (${(bidChangePercent * 100).toFixed(0)}%)`);
        continue;
      }

      if (!dryRun) {
        try {
          // Apply the bid change
          await this.adsClient.updateKeywordBids(customerId, [{
            keywordId: rec.keywordId,
            adGroupId: '', // Would need to get this from keyword data
            newBidMicros: Math.round(rec.recommendedBid * 1000000),
          }]);

          // Log the change
          await supabase.from('google_ads_bid_changes').insert({
            customer_id: customerId,
            keyword_id: rec.keywordId,
            old_bid_micros: Math.round(rec.currentBid * 1000000),
            new_bid_micros: Math.round(rec.recommendedBid * 1000000),
            reason: rec.reason,
            confidence: rec.confidence,
            applied_at: new Date().toISOString(),
          });

          applied++;
        } catch (error) {
          errors.push(`${rec.keywordText}: ${error}`);
        }
      } else {
        applied++; // Count as applied in dry run
      }
    }

    return { applied, skipped, errors };
  }

  /**
   * Add negative keywords automatically
   */
  async addNegativeKeywords(
    customerId: string,
    negativeKeywords: NegativeKeyword[],
    options: {
      minConfidence?: number;
      dryRun?: boolean;
    } = {}
  ): Promise<{ added: number; skipped: number; errors: string[] }> {
    const { minConfidence = 0.8, dryRun = true } = options;
    
    let added = 0;
    let skipped = 0;
    const errors: string[] = [];

    for (const neg of negativeKeywords) {
      if (neg.confidence < minConfidence) {
        skipped++;
        continue;
      }

      if (!dryRun) {
        try {
          // Note: Actual implementation would use Google Ads API to add negative keyword
          // For now, just log it
          console.log(`Would add negative keyword: ${neg.searchTerm}`);
          added++;
        } catch (error) {
          errors.push(`${neg.searchTerm}: ${error}`);
        }
      } else {
        added++; // Count as added in dry run
      }
    }

    return { added, skipped, errors };
  }
}

// ============================================================================
// Factory Function
// ============================================================================

/**
 * Create Google Ads AI Optimizer instance
 */
export function createGoogleAdsAIOptimizer(adsClient: GoogleAdsClient): GoogleAdsAIOptimizer {
  return new GoogleAdsAIOptimizer(adsClient);
}
